import React, { useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
  IonItem,
  IonIcon,
} from "@ionic/react";
import { lockClosedOutline } from "ionicons/icons";
import { useForm, Controller } from "react-hook-form";
import { useIonRouter } from "@ionic/react";
import { useLogin, useSendLoginCode } from "@/hooks";
import { getErrorMessage } from "@/api/client";
import { registrationStorage } from "@/services/storage";
import TwoFactorChallenge from "../TwoFactorChallenge";
import "./Auth.css";
import { Did } from "@/types";
import AppLogo from "@components/AppLogo.tsx";

interface PasswordForm {
  password: string;
}

const EnterPassword: React.FC = () => {
  const router = useIonRouter();
  const [email, setEmail] = useState<string>("");
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [userId, setUserId] = useState<Did | null>(null);
  const loginMutation = useLogin();
  const sendCodeMutation = useSendLoginCode();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordForm>({
    defaultValues: {
      password: "",
    },
  });

  useEffect(() => {
    // Load email from storage
    registrationStorage.getEmail().then((storedEmail) => {
      if (storedEmail) {
        setEmail(storedEmail);
      } else {
        // No email stored, go back to welcome
        router.push("/auth/welcome", "back", "replace");
      }
    });
  }, [router]);

  const onSubmit = async (data: PasswordForm) => {
    if (!email) return;

    try {
      const result = await loginMutation.mutateAsync({
        email,
        password: data.password,
      });

      // Check if 2FA is required
      if (result.two_factor_required && result.user_id) {
        setTwoFactorRequired(true);
        setUserId(result.user_id);
      }
      // Navigation for successful login handled by hook
    } catch {
      // Error handled by mutation
    }
  };

  const handleSendCode = async () => {
    if (!email) return;
    try {
      await sendCodeMutation.mutateAsync(email);
      router.push("/auth/enter-code", "forward");
    } catch {
      // Error handled by mutation
    }
  };

  const handleBack = () => {
    router.push("/auth/enter-code", "back");
  };

  const handleBackFrom2FA = () => {
    setTwoFactorRequired(false);
    setUserId(null);
  };

  // If 2FA is required, show the TwoFactorChallenge component
  if (twoFactorRequired && userId) {
    return <TwoFactorChallenge userId={userId} onBack={handleBackFrom2FA} />;
  }

  return (
    <IonPage>
      <IonContent className="auth-content ion-padding">
        <div className="auth-container">
          <div className="auth-header">
            <AppLogo />
            <IonText color="dark">
              <h1>Inloggen met wachtwoord</h1>
              <p>
                Voer je wachtwoord in voor
                <br />
                <strong>{email}</strong>
              </p>
            </IonText>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <IonItem className="auth-input" lines="none">
              <IonIcon icon={lockClosedOutline} slot="start" />
              <Controller
                name="password"
                control={control}
                rules={{
                  required: "Wachtwoord is verplicht",
                }}
                render={({ field }) => (
                  <IonInput
                    {...field}
                    type="password"
                    placeholder="Wachtwoord"
                    autocomplete="current-password"
                    onIonInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </IonItem>
            {errors.password && (
              <IonText color="danger" className="error-text">
                <p>{errors.password.message}</p>
              </IonText>
            )}

            {loginMutation.error && (
              <IonText color="danger" className="error-text">
                <p>{getErrorMessage(loginMutation.error)}</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              type="submit"
              className="auth-button"
              disabled={loginMutation.isPending}
            >
              {loginMutation.isPending ? (
                <>
                  <IonSpinner name="crescent" />
                  <span style={{ marginLeft: "8px" }}>Inloggen...</span>
                </>
              ) : (
                "Inloggen"
              )}
            </IonButton>

            <div className="auth-link">
              <IonButton
                fill="clear"
                size="small"
                onClick={handleSendCode}
                disabled={sendCodeMutation.isPending}
              >
                {sendCodeMutation.isPending
                  ? "Code versturen..."
                  : "Stuur mij een inlogcode"}
              </IonButton>
            </div>

            {sendCodeMutation.error && (
              <IonText color="danger" className="error-text">
                <p style={{ textAlign: "center" }}>
                  {getErrorMessage(sendCodeMutation.error)}
                </p>
              </IonText>
            )}

            <div className="auth-link">
              <IonButton fill="clear" size="small" onClick={handleBack}>
                Terug
              </IonButton>
            </div>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EnterPassword;
